/* 卖点轮播行为层
   以 Custom Element 承接 Theme Editor 生命周期，内部使用主题已 vendored 的 Swiper。
   所有控件均以自身为作用域查询，支持同页多实例。 */

class SellingPointsCarousel extends HTMLElement {
  connectedCallback() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.container = this.querySelector('[data-carousel-viewport]');
    this.progressTrack = this.querySelector('[data-slider-progress-track]');
    this.progressFill = this.querySelector('[data-slider-progress]');
    this.pauseButton = this.querySelector('[data-slider-pause]');

    if (!this.container) return;

    this.onPauseClick = this.onPauseClick.bind(this);
    this.updateProgress = this.updateProgress.bind(this);

    this.initSwiper();

    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', this.onPauseClick);
    }
  }

  disconnectedCallback() {
    if (this.pauseButton) {
      this.pauseButton.removeEventListener('click', this.onPauseClick);
    }
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
    }
    this.swiper = null;
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.reduceMotion;
  }

  initSwiper() {
    if (typeof window.Swiper !== 'function') {
      // Swiper 未就绪时保持原生横向滚动降级，不抛错、不阻断内容访问
      this.classList.add('selling-points-carousel--no-script');
      return;
    }

    const interval = parseInt(this.dataset.interval, 10) || 5000;
    // 轨道左右留白由 CSS 的 padding-inline 提供；Swiper 的 size 含 padding，
    // 需要补 slidesOffsetAfter = 2 × 留白，末张才会停在右侧留白处而不是被裁掉
    const gutter = parseFloat(getComputedStyle(this.container).paddingLeft) || 0;

    this.swiper = new window.Swiper(this.container, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      slidesOffsetAfter: gutter * 2,
      loop: this.dataset.loop === 'true',
      watchOverflow: true,
      observer: true,
      observeParents: true,
      speed: this.reduceMotion ? 0 : 600,
      a11y: {
        enabled: true,
        prevSlideMessage: this.dataset.prevLabel || '',
        nextSlideMessage: this.dataset.nextLabel || '',
      },
      keyboard: { enabled: true, onlyInViewport: true },
      autoplay: this.autoplayEnabled
        ? { delay: interval, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      navigation: {
        prevEl: this.querySelector('[data-slider-previous]'),
        nextEl: this.querySelector('[data-slider-next]'),
        disabledClass: 'is-disabled',
      },
      on: {
        init: this.updateProgress,
        resize: this.updateProgress,
        progress: this.updateProgress,
        slidesLengthChange: this.updateProgress,
      },
    });
  }

  updateProgress() {
    if (!this.progressFill || !this.swiper) return;

    const steps = Math.max(this.swiper.snapGrid ? this.swiper.snapGrid.length : 1, 1);
    const fraction = Math.min(1 / steps, 1);
    const progress = Number.isFinite(this.swiper.progress) ? this.swiper.progress : 0;

    this.progressFill.style.width = `${fraction * 100}%`;
    this.progressFill.style.transform = `translateX(${(progress * (1 - fraction) * 100) / fraction}%)`;
  }

  onPauseClick() {
    if (!this.swiper || !this.swiper.autoplay) return;

    const willPause = this.swiper.autoplay.running;
    if (willPause) {
      this.swiper.autoplay.stop();
    } else {
      this.swiper.autoplay.start();
    }

    this.pauseButton.setAttribute('aria-pressed', willPause ? 'true' : 'false');
    const label = willPause ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel;
    if (label) this.pauseButton.setAttribute('aria-label', label);
    this.pauseButton.classList.toggle('is-paused', willPause);
  }
}

if (!customElements.get('selling-points-carousel')) {
  customElements.define('selling-points-carousel', SellingPointsCarousel);
}
